import { useEffect, useState } from "react";
import { api } from "@/lib/admin/api";

/**
 * The public website now lives on the SAME origin as the admin (monolithic React SPA).
 * Defaults map to:  /  (homepage),  /menu  (full menu)
 * Settings can override (rarely needed) to point to an external Hostinger mirror.
 */
const DEFAULT = "/";

let _cached = null;
let _inflight = null;

/** Hook returning the live base URL ("" or "/" means same-origin SPA). */
export function useLiveSiteBaseUrl() {
  const [url, setUrl] = useState(_cached ?? DEFAULT);

  useEffect(() => {
    if (_cached !== null) { setUrl(_cached); return; }
    if (!_inflight) {
      _inflight = api.getContent().then(
        (c) => { _cached = (c && c.live_site_base_url) || DEFAULT; return _cached; },
        () => { _cached = DEFAULT; return DEFAULT; }
      );
    }
    _inflight.then((u) => setUrl(u));
    const onChange = (e) => { if (e?.detail?.url !== undefined) { _cached = e.detail.url; setUrl(e.detail.url); } };
    window.addEventListener("admin:live_site_base_url:changed", onChange);
    return () => window.removeEventListener("admin:live_site_base_url:changed", onChange);
  }, []);

  return url;
}

/** When base is "/" → use SPA route /menu#cat. When base is external Hostinger → menu.html#cat. */
export function liveMenuUrl(baseUrl, categoryId) {
  const base = (baseUrl ?? DEFAULT) || DEFAULT;
  if (base === "/" || base === "") return `/menu#${categoryId || "flower"}`;
  const stripped = base.replace(/\/+$/, "");
  if (/index\.html$|menu\.html$/.test(stripped)) return `${stripped.replace(/\/(index|menu)\.html$/, "")}/menu.html#${categoryId || "flower"}`;
  return `${stripped}/menu.html#${categoryId || "flower"}`;
}

/** Build a homepage URL (with optional anchor). */
export function liveIndexUrl(baseUrl, anchor = "") {
  const base = (baseUrl ?? DEFAULT) || DEFAULT;
  if (base === "/" || base === "") return `/${anchor ? "#" + anchor : ""}`;
  const stripped = base.replace(/\/+$/, "");
  if (/index\.html$/.test(stripped)) return stripped + (anchor ? "#" + anchor : "");
  return `${stripped}/${anchor ? "#" + anchor : ""}`;
}

export function announceLiveSiteBaseUrl(url) {
  _cached = url;
  window.dispatchEvent(new CustomEvent("admin:live_site_base_url:changed", { detail: { url } }));
}

export const LIVE_SITE_DEFAULT = DEFAULT;
